import React from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { AutoFocusPlugin } from '@lexical/react/LexicalAutoFocusPlugin';
import { mergeRegister } from '@lexical/utils';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import {
  $getSelectionStyleValueForProperty,
  $patchStyleText,
} from '@lexical/selection';
import { Bold, Italic } from 'lucide-react';
import { Textarea } from '../../../ui/textarea';
import {
  coerceLexicalStateString,
  lexicalStateToPlainText,
  lexicalPlainTextToSerializedState,
} from '../../../../lib/lexicalRichText';

export type RichTextFontOption = {
  value: string;
  label: string;
  previewFamily?: string;
  styleValue?: string;
};

type RichTextEditorProps = {
  id: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  onRequestClose?: () => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  surfaceClassName?: string;
  editorClassName?: string;
  toolbarClassName?: string;
  showToolbar?: boolean;
  showFontFamilyControl?: boolean;
  autoFocus?: boolean;
  fontOptions?: RichTextFontOption[];
};

const editorTheme = {
  paragraph: 'mb-2 leading-7',
};

const INHERIT_FONT_VALUE = '__inherit__';

function normalizeFontStyleValue(value: string | null | undefined) {
  return String(value ?? '')
    .replace(/["']/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function resolveFontCssValue(option: RichTextFontOption) {
  return option.styleValue ?? option.previewFamily ?? option.label;
}

const SyncEditorStatePlugin: React.FC<{
  value: string;
  onParseError: () => void;
}> = ({ value, onParseError }) => {
  const [editor] = useLexicalComposerContext();
  const lastAppliedRef = React.useRef(value);

  React.useEffect(() => {
    if (value === lastAppliedRef.current) return;
    const currentSerialized = JSON.stringify(editor.getEditorState().toJSON());
    if (currentSerialized === value) {
      lastAppliedRef.current = value;
      return;
    }
    try {
      const parsed = editor.parseEditorState(value);
      editor.setEditorState(parsed);
      lastAppliedRef.current = value;
    } catch {
      onParseError();
    }
  }, [editor, onParseError, value]);

  return null;
};

const ToolbarPlugin: React.FC<{
  disabled?: boolean;
  showFontFamilyControl?: boolean;
  fontOptions: RichTextFontOption[];
}> = ({ disabled = false, showFontFamilyControl = true, fontOptions }) => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = React.useState(false);
  const [isItalic, setIsItalic] = React.useState(false);
  const [fontValue, setFontValue] = React.useState(INHERIT_FONT_VALUE);

  const updateToolbar = React.useCallback(() => {
    editor.getEditorState().read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        setIsBold(false);
        setIsItalic(false);
        setFontValue(INHERIT_FONT_VALUE);
        return;
      }

      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));

      const selectedFont = normalizeFontStyleValue(
        $getSelectionStyleValueForProperty(selection, 'font-family', '')
      );

      if (!selectedFont) {
        setFontValue(INHERIT_FONT_VALUE);
        return;
      }

      const match = fontOptions.find(
        (option) => normalizeFontStyleValue(resolveFontCssValue(option)) === selectedFont
      );
      setFontValue(match?.value ?? INHERIT_FONT_VALUE);
    });
  }, [editor, fontOptions]);

  React.useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(() => {
          updateToolbar();
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            updateToolbar();
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor, updateToolbar]
  );

  const applyFontFamily = React.useCallback(
    (nextValue: string) => {
      setFontValue(nextValue);
      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        if (nextValue === INHERIT_FONT_VALUE) {
          $patchStyleText(selection, { 'font-family': null });
          return;
        }
        const option = fontOptions.find((entry) => entry.value === nextValue);
        if (!option) return;
        $patchStyleText(selection, {
          'font-family': resolveFontCssValue(option),
        });
      });
      editor.focus();
    },
    [editor, fontOptions]
  );

  return (
    <div
      className="flex flex-wrap items-center gap-2 border-b border-stone-200 bg-stone-50/90 px-3 py-2 font-sans"
      data-builder-allow-interaction
      data-builder-text-editor="true"
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      {showFontFamilyControl ? (
        <select
          value={fontValue}
          disabled={disabled}
          onChange={(event) => applyFontFamily(event.target.value)}
          onPointerDown={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          className="min-w-[160px] rounded-md border border-stone-300 bg-white px-2.5 py-1.5 font-sans text-xs text-stone-800 shadow-sm outline-none focus:border-blue-500"
          aria-label="Font family"
        >
          <option value={INHERIT_FONT_VALUE}>Block default</option>
          {fontOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
        }}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-md border text-stone-700 shadow-sm transition ${
          isBold
            ? 'border-blue-500 bg-blue-600 text-white'
            : 'border-stone-300 bg-white hover:bg-stone-100'
        }`}
        aria-label="Bold"
        title="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        disabled={disabled}
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.stopPropagation();
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
        }}
        className={`inline-flex h-8 w-8 items-center justify-center rounded-md border text-stone-700 shadow-sm transition ${
          isItalic
            ? 'border-blue-500 bg-blue-600 text-white'
            : 'border-stone-300 bg-white hover:bg-stone-100'
        }`}
        aria-label="Italic"
        title="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  id,
  value,
  onChange,
  onBlur,
  onRequestClose,
  placeholder,
  disabled,
  className,
  surfaceClassName,
  editorClassName,
  toolbarClassName,
  showToolbar = true,
  showFontFamilyControl = true,
  autoFocus = false,
  fontOptions = [],
}) => {
  const normalizedValue = React.useMemo(
    () => coerceLexicalStateString(value, ''),
    [value]
  );
  const initialEditorStateRef = React.useRef(normalizedValue);
  const [fallbackMode, setFallbackMode] = React.useState(false);
  const [fallbackText, setFallbackText] = React.useState(() =>
    lexicalStateToPlainText(normalizedValue)
  );
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const lastSerializedRef = React.useRef(normalizedValue);
  const suppressBlurUntilRef = React.useRef(0);
  const hasFocusedInsideRef = React.useRef(false);

  React.useEffect(() => {
    const nextText = lexicalStateToPlainText(normalizedValue);
    setFallbackText(nextText);
    lastSerializedRef.current = normalizedValue;
  }, [normalizedValue]);

  React.useEffect(() => {
    if (!autoFocus || fallbackMode) return;
    suppressBlurUntilRef.current = Date.now() + 250;
    const frameId = window.requestAnimationFrame(() => {
      const editorNode = wrapperRef.current?.querySelector('[contenteditable="true"]') as HTMLElement | null;
      editorNode?.focus();
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [autoFocus, fallbackMode]);

  const initialConfig = React.useMemo(
    () => ({
      namespace: `${id}-lexical`,
      editorState: initialEditorStateRef.current,
      editable: !disabled,
      theme: editorTheme,
      onError: () => {
        setFallbackMode(true);
      },
    }),
    [disabled, id]
  );

  const handleBlurCapture = React.useCallback(() => {
    if (!onBlur) return;
    if (!hasFocusedInsideRef.current) return;
    if (Date.now() < suppressBlurUntilRef.current) return;
    window.requestAnimationFrame(() => {
      if (!wrapperRef.current?.contains(document.activeElement)) {
        onBlur();
      }
    });
  }, [onBlur]);

  const handleFocusCapture = React.useCallback(() => {
    hasFocusedInsideRef.current = true;
  }, []);

  const stopEditorEventPropagation = React.useCallback((event: React.SyntheticEvent) => {
    event.stopPropagation();
  }, []);

  const handleEditorKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestClose?.();
      }
    },
    [onRequestClose]
  );

  if (fallbackMode) {
    return (
      <div
        ref={wrapperRef}
        className={className}
        onBlurCapture={handleBlurCapture}
        onFocusCapture={handleFocusCapture}
        onPointerDown={stopEditorEventPropagation}
        onMouseDown={stopEditorEventPropagation}
        onClick={stopEditorEventPropagation}
        onDoubleClick={stopEditorEventPropagation}
        onKeyDown={handleEditorKeyDown}
        data-builder-text-editor="true"
        data-builder-allow-interaction
      >
        <Textarea
          id={id}
          rows={8}
          value={fallbackText}
          onChange={(event) => {
            const nextText = event.target.value;
            setFallbackText(nextText);
            onChange(lexicalPlainTextToSerializedState(nextText));
          }}
          placeholder={placeholder}
          disabled={disabled}
          onBlur={onBlur}
          className={editorClassName}
        />
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      className={className}
      onFocusCapture={handleFocusCapture}
      onBlurCapture={handleBlurCapture}
      data-builder-text-editor="true"
      data-builder-allow-interaction
      onPointerDown={stopEditorEventPropagation}
      onMouseDown={stopEditorEventPropagation}
      onClick={stopEditorEventPropagation}
      onDoubleClick={stopEditorEventPropagation}
    >
      <div
        className={`relative overflow-visible rounded-md border border-stone-300 bg-white ${surfaceClassName ?? ''}`.trim()}
      >
        <LexicalComposer initialConfig={initialConfig}>
          {showToolbar ? (
            <div className={toolbarClassName}>
              <ToolbarPlugin
                disabled={disabled}
                showFontFamilyControl={showFontFamilyControl}
                fontOptions={fontOptions}
              />
            </div>
          ) : null}
          {autoFocus ? <AutoFocusPlugin /> : null}
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                className={`min-h-[168px] px-3 py-2 font-sans text-sm text-stone-900 outline-none ${editorClassName ?? ''}`.trim()}
                onKeyDown={handleEditorKeyDown}
                onPointerDown={stopEditorEventPropagation}
                onMouseDown={stopEditorEventPropagation}
                onClick={stopEditorEventPropagation}
                onDoubleClick={stopEditorEventPropagation}
                data-builder-text-editor="true"
                data-builder-allow-interaction
              />
            }
            placeholder={
              <div className="pointer-events-none absolute px-3 py-2 text-sm text-stone-400">
                {placeholder ?? 'Write your content...'}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <SyncEditorStatePlugin value={normalizedValue} onParseError={() => setFallbackMode(true)} />
          <OnChangePlugin
            ignoreSelectionChange
            onChange={(editorState) => {
              const serialized = JSON.stringify(editorState.toJSON());
              if (serialized === lastSerializedRef.current) return;
              lastSerializedRef.current = serialized;
              onChange(serialized);
            }}
          />
        </LexicalComposer>
      </div>
    </div>
  );
};
